import React from 'react';
import ReactDOM from 'react-dom';
import {
	Avatar,
	AVATAR_DEFAULT_DNA,
	AVATAR_OPTIONS,
	PALETTE,
	exportAvatarPngBlob,
	normalizeAvatarDna,
} from 'components/AvatarBuilder';

const NEUTRAL_TOP = ['shortHair', 'longHair', 'noHair', 'hat'];

const pickRandomValue = (options = []) => {
	if (!options.length) {
		return null;
	}
	const idx = Math.floor(Math.random() * options.length);
	return options[idx].value ?? options[idx];
};

const buildRandomDna = () => {
	const topOptions = NEUTRAL_TOP;
	const facialHairOptions = AVATAR_OPTIONS.facialHair.map((item) => item.value);
	const accessoriesOptions = AVATAR_OPTIONS.accessories.map((item) => item.value);

	const dna = {
		...AVATAR_DEFAULT_DNA,
		style: 'circle',
		bgColor: pickRandomValue(AVATAR_OPTIONS.bgColor),
		skin: pickRandomValue(AVATAR_OPTIONS.skin),
		top: pickRandomValue(topOptions),
		hairColor: pickRandomValue(AVATAR_OPTIONS.hairColor),
		accessories: pickRandomValue(accessoriesOptions),
		facialHair: pickRandomValue(facialHairOptions),
		facialHairColor: pickRandomValue(AVATAR_OPTIONS.facialHairColor),
		clothes: pickRandomValue(AVATAR_OPTIONS.clothes),
		clothesColor: pickRandomValue(AVATAR_OPTIONS.clothesColor),
		eyes: pickRandomValue(AVATAR_OPTIONS.eyes),
		eyebrow: pickRandomValue(AVATAR_OPTIONS.eyebrow),
		mouth: pickRandomValue(AVATAR_OPTIONS.mouth),
	};

	return normalizeAvatarDna(dna);
};

const blobToDataUrl = (blob) => new Promise((resolve, reject) => {
	const reader = new FileReader();
	reader.onloadend = () => resolve(reader.result);
	reader.onerror = () => reject(new Error('Failed to read PNG'));
	reader.readAsDataURL(blob);
});

const renderAvatarSvg = (dna) => new Promise((resolve, reject) => {
	if (typeof document === 'undefined') {
		reject(new Error('DOM is not available'));
		return;
	}

	const container = document.createElement('div');
	container.style.position = 'fixed';
	container.style.left = '-10000px';
	container.style.top = '-10000px';
	container.style.width = '0';
	container.style.height = '0';
	container.style.overflow = 'hidden';
	document.body.appendChild(container);

	let wrapper = null;

	const cleanup = () => {
		ReactDOM.unmountComponentAtNode(container);
		container.remove();
	};

	const onReady = () => {
		const svg = wrapper?.querySelector?.('svg');
		if (!svg) {
			cleanup();
			reject(new Error('SVG is not available'));
			return;
		}

		resolve({ svg, cleanup });
	};

	ReactDOM.render(<Avatar ref={(el) => { wrapper = el; }} dna={dna} size={160} />, container, onReady);
});

export const generateRandomAvatarPayload = async () => {
	const dna = buildRandomDna();
	const { svg, cleanup } = await renderAvatarSvg(dna);

	try {
		const background = dna.style === 'circle' ? PALETTE.bg[dna.bgColor] : null;
		const pngBlob = await exportAvatarPngBlob(svg, {
			outSize: 128,
			scale: 4,
			background,
		});
		const pngBase64 = await blobToDataUrl(pngBlob);
		return { dna, pngBase64 };
	} finally {
		cleanup();
	}
};
